import { X } from 'lucide-react';
import { useId, type ReactElement } from 'react';

import { cn } from '@/components/ui/cn';
import type { Role } from '@/features/roles-access/types';

import { companiesSummary, userDisplayName, userStatusBadge } from '../presentation';
import type { AdminUser } from '../types';
import { Select } from '@/components/ui/Select';

export interface UserRowPolicy {
  /** Es el propio usuario que administra: no puede desactivarse. */
  isSelf: boolean;
  /** Usuario de otras empresas con un admin de una sola: solo lo gestiona uno del grupo. */
  lockedMultiCompany: boolean;
  /** Tiene permiso para asignar y quitar roles. */
  canManageRoles: boolean;
}

interface Props {
  user: AdminUser;
  roles: Role[];
  policy: UserRowPolicy;
  /** Mutación en curso para este usuario: deshabilita sus controles (sin doble-submit). */
  busy: boolean;
  onAssign: (roleId: string) => void;
  onRemoveRequest: (roleCode: string, roleName: string) => void;
  onDeactivateRequest: () => void;
  onReactivate: () => void;
}

/** Un usuario: identidad, empresas (del LMS), estado, roles y acciones. En móvil es
 *  una tarjeta; desde 900px, una fila de tabla (`.t-cards`). Presentacional. */
export function UserRow({
  user,
  roles,
  policy,
  busy,
  onAssign,
  onRemoveRequest,
  onDeactivateRequest,
  onReactivate,
}: Props): ReactElement {
  const selectId = useId();
  const name = userDisplayName(user);
  const status = userStatusBadge(user.status);
  const companies = companiesSummary(user.companies, user.otherCompaniesCount);
  const manageable = !policy.lockedMultiCompany;
  const assignable = roles.filter((r) => !user.roles.some((ur) => ur.code === r.code));

  return (
    <tr data-testid={`user-${user.id}`}>
      <td data-label="Usuario">
        <div className="who stack">
          <span className="nm">{name}</span>
          <span className="dim-mono">{user.email}</span>
        </div>
      </td>
      <td data-label="Empresas">
        {companies.empty ? (
          <span className="soft">{companies.empty}</span>
        ) : (
          <div className="chips">
            {companies.names.map((n) => (
              <span key={n} className="rolechip">
                {n}
              </span>
            ))}
            {companies.more && <span className="soft">{companies.more}</span>}
          </div>
        )}
      </td>
      <td data-label="Estado">
        <span className={cn('badge', status.cls)}>
          <span className="dotb" />
          {status.label}
        </span>
      </td>
      <td data-label="Roles">
        {user.roles.length === 0 ? (
          <span className="soft">Sin roles</span>
        ) : (
          <div className="chips">
            {user.roles.map((r) => (
              <span key={r.code} className="rolechip">
                {r.name}
                {policy.canManageRoles && manageable && (
                  <button
                    type="button"
                    className="rolechip-x"
                    aria-label={`Quitar rol ${r.name} de ${name}`}
                    disabled={busy}
                    onClick={() => onRemoveRequest(r.code, r.name)}
                  >
                    <X aria-hidden="true" size={13} />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </td>
      <td data-label="Acciones" className="t-act">
        <div className="row-actions">
          {policy.canManageRoles && (
            <>
              <label className="sr-only" htmlFor={selectId}>
                Asignar un rol a {name}
              </label>
              <Select
                id={selectId}
                className="sel-inline"
                value=""
                disabled={busy || !manageable || assignable.length === 0}
                placeholder={assignable.length === 0 ? 'Todos los roles asignados' : 'Asignar rol…'}
                options={assignable.map((r) => ({ value: r.id, label: r.name }))}
                onChange={onAssign}
              />
            </>
          )}
          {policy.isSelf ? (
            <span className="soft">Tu usuario</span>
          ) : !manageable ? null : user.status === 'inactive' ? (
            <button
              type="button"
              className="btn sm mut"
              disabled={busy}
              aria-label={`Reactivar a ${name}`}
              onClick={onReactivate}
            >
              Reactivar
            </button>
          ) : (
            <button
              type="button"
              className="btn sm danger"
              disabled={busy}
              aria-label={`Desactivar a ${name}`}
              onClick={onDeactivateRequest}
            >
              Desactivar
            </button>
          )}
        </div>
        {policy.lockedMultiCompany && (
          <p className="soft row-note">
            Pertenece también a otras empresas: lo gestiona un administrador del grupo.
          </p>
        )}
      </td>
    </tr>
  );
}
